import {
  DOWNLOAD,
  ELECTRON,
  BOOK_LAUNCH_MODES,
  FILE_TYPES,
  BOOK_DOWNLOADING_STATUS,
  VERSION_COMPARE,
  PARAMS
} from '../../const';
import { requireFromElectron } from '../../services/crossPlatform/crossPlatformService';
import * as contentPathUtils from '../../utils/contentPathUtils';
import { getLogger } from '../logger';
import * as _ from 'lodash';
import * as contentUtils from '../../utils/contentUtils';
import stringHash from 'string-hash';
import { join } from 'path';
import { getCdnVersion, getSkinVersion } from '../api/backofficeApi';
import { API_CDN_DOWNLOAD, API_BACKOFFICE_SKIN_DOWNLOAD } from '../api/apiConst';
import { downloadFile } from '../domain/contentService';
import { v4 } from 'uuid';
import * as path from 'path';

const request = requireFromElectron('request');
const unzip = requireFromElectron('unzip-stream');
const inly = requireFromElectron('inly');
const electron = requireFromElectron('electron');
const fs = requireFromElectron('fs-extra');
const debug = getLogger('services:file:fileSystemService');

/**
 * Checks if a folder exist
 * @param folder
 * @returns {*}
 */
export let existsFolder = folder => {
  return fs.existsSync(folder);
};

/**
 * Checks if a file exist
 * @param file
 * @returns {*}
 */
export let existsFile = file => {
  if (fs) {
    return fs.existsSync(file);
  }
};

/**
 * Creates a folder into the file system if it does not exist
 * @param pathToFolder
 */
export async function createFolder(pathToFolder) {
  await fs.ensureDir(pathToFolder);
}

export let remove = pathToFolder => {
  if (fs.existsSync(pathToFolder)) {
    fs.removeSync(pathToFolder);
  }
};

export async function writeTextFile(filePath, text) {
  if (!filePath || !text) {
    debug.error('[writeTextFile] No parameter specified filePath: "%s" | text: %s', filePath, text);
    return;
  }

  await fs.outputFile(filePath, text);
}

/**
 * Rename file
 * @param oldPath
 * @param newPath
 * @returns {*}
 */
export let renameFile = (oldPath, newPath) => {
  if (fs.existsSync(oldPath)) {
    return fs.renameSync(oldPath, newPath);
  }
};

/**
 * @param {object} data
 * @param {string} data.url
 * @param {string} [data.authToken]
 */
export let httpRequestOptions = data => {
  const { url, authToken, contentType } = data;
  const options = {
    uri: url
  };

  if (!_.isEmpty(authToken)) {
    if (!options.headers) options.headers = {};
    options.headers.Authorization = `Bearer ${authToken}`;
  }

  if (!_.isEmpty(contentType)) {
    if (!options.headers) options.headers = {};
    options.headers.ContentType = contentType;
  }

  return options;
};

export let httpGetWithProgress = async (options, onStart, onData, onEnd, onError, pathToFile) => {
  if (!options.uri) {
    debug.error('[httpGetWithProgress] Missing resourceUrl %s', options.uri);
    onError(new Error('[httpGetWithProgress] Missing resourceUrl %s', options.uri));
  }

  if (existsFile(pathToFile)) {
    removeFile(pathToFile);
  }

  request(options)
    .on('response', response => {
      if (response.statusCode >= 400) {
        const error = new Error('[httpGetWithProgress] Response error %O', response);
        error.responseStatusCode = response.statusCode;
        onError(error);
      }

      let contentLength = parseInt(response.headers['content-length'], 10);
      let { size, downloaded, percentage } = getDownloadProcessVariables(pathToFile, contentLength);

      onStart(size, percentage);

      response.on('data', chunk => {
        downloaded += chunk.length;
        percentage = Math.floor((downloaded * 100) / size);
        onData(response, percentage);
      });
    })
    .pipe(fs.createWriteStream(pathToFile))
    .on('finish', async () => await removeFile(getTemporaryFileName(pathToFile)))
    .on('close', onEnd)
    .on('error', onError);
};

let getDownloadProcessVariables = (pathToFile, contentLength) => {
  let size = !_.isNaN(contentLength) ? contentLength : 0;
  let downloaded = 0;
  let percentage = 0;

  /** Create a temporary file unless it already exists */
  const tmpFileName = getTemporaryFileName(pathToFile);
  if (existsFile(tmpFileName)) {
    const totalSizeSaved = fs.readJsonSync(tmpFileName).size;
    const partialDownloadedSaved = fs.statSync(pathToFile).size;

    if (totalSizeSaved && totalSizeSaved > 0) size = totalSizeSaved;
    if (partialDownloadedSaved && partialDownloadedSaved > 0) downloaded = partialDownloadedSaved;
    if (size > 0) percentage = Math.floor((downloaded * 100) / size);
  } else {
    if (_.isNaN(contentLength) && existsFile(pathToFile)) {
      size = fs.statSync(pathToFile).size;
      downloaded = size;
      percentage = DOWNLOAD.COMPLETED;
    }
    fs.outputJsonSync(tmpFileName, {});
  }

  return { size, downloaded, percentage };
};

export let getHttpRangeHeader = (options, pathToFile) => {
  const stat = fs.statSync(pathToFile);
  const fromByte = stat.size;
  return `bytes=${fromByte}-`;
};

export let unZipContentWithPercentage = async (pathToFile, destination, onProgress) => {
  const stat = fs.statSync(pathToFile);
  await new Promise((resolve, reject) => {
    if (!fs.existsSync(destination)) {
      fs.mkdirSync(destination);
    }

    fs.createReadStream(pathToFile)
      .pipe(unzip.Extract({ path: destination }))
      .on('finish', () => {
        onProgress({
          length: stat.size,
          percentage: 100
        });
      })
      .on('close', resolve)
      .on('error', reject);
  });
};

export let unZipFiles = async (pathToFile, destination, onProgress) => {
  // This function is necessary because the other unzip library
  // throws invalid signature error when tries to unzip skins
  const stat = fs.statSync(pathToFile);
  await new Promise((resolve, reject) => {
    if (!fs.existsSync(destination)) {
      fs.mkdirSync(destination);
    }
    inly(pathToFile, destination)
      .on('progress', res => {
        onProgress({
          length: stat.size,
          percentage: res
        });
      })
      .on('error', reject)
      .on('end', resolve);
  });
};

/**
 * Gets the configured as base url
 * @returns {string}
 */
export let getBaseUrl = () => {
  if (electron) {
    const path = electron.remote.app.getPath(ELECTRON.DATA_DIR);
    return path.split('\\').join('/');
  }
};

export let getElectronResourcesPath = () => {
  if (electron) {
    return electron.remote.process.resourcesPath;
  }
};

/**
 * Removes a file from the file system
 * @param pathToFile
 */
export function removeFile(pathToFile) {
  if (fs.existsSync(pathToFile)) {
    return fs.removeSync(pathToFile);
  }
}

/**
 * Removes the content of a book from the file system
 * @param userId
 * @param classId
 * @param bookId
 * @param bookActivityId
 * @param unitActivityId
 */
export let removeDownloadingContent = (userId, classId, bookId, bookActivityId, singleUnitModeUnitId) => {
  localStorage.setItem('cancel_' + bookId, 'true');
  if (singleUnitModeUnitId) {
    removeUnitContent(userId, classId, bookId, bookActivityId, singleUnitModeUnitId);
  } else {
    removeBookContent(userId, classId, bookId, bookActivityId);
  }
};

/**
 * Removes the content of a book from the file system
 * @param userId
 * @param classId
 * @param bookId
 * @param bookActivityId
 */
let removeBookContent = (userId, classId, bookId, bookActivityId) => {
  const bookContext = contentUtils.createBookContext(userId, classId, bookId, bookActivityId);

  const bookPath = contentPathUtils.getBookFolderPath(bookContext);
  return remove(bookPath);
};

/**
 * Removes the content of a book from the file system
 * @param userId
 * @param classId
 * @param bookId
 * @param bookActivityId
 * @param unitActivityId
 */
let removeUnitContent = (userId, classId, bookId, bookActivityId, unitActivityId) => {
  const unitContext = contentUtils.createUnitContext(userId, classId, bookId, bookActivityId, unitActivityId);

  const unitPath = contentPathUtils.getUnitFolderPath(unitContext);
  return remove(unitPath);
};

export let removeClassroomContent = (classId, userId) => {
  remove(`${getBaseUrl()}/${userId}/${classId}`);
};

export async function saveJsonToFile(fileName, json) {
  if (!fileName) {
    debug.error('[saveJsonToFile] No filename specified');
  }

  if (!json) {
    debug.error('[saveJsonToFile] No JSON specified');
  }

  await fs.outputJson(fileName, json);
}

export async function readJsonFile(filePath) {
  if (await fs.pathExists(filePath)) {
    return await fs.readJson(filePath);
  }
}

export function getTemporaryFileName(fileName) {
  return `${fileName}.temp.json`;
}

export function removeObsoleteDownloadedClassroomBooks(userId, classId, books) {
  const classFolder = contentPathUtils.getClassroomFolderPath(userId, classId);

  if (!existsFolder(classFolder)) return;

  const downloadedBooks = fs.readdirSync(classFolder);

  const remoteBooks = [];
  books.map(book => remoteBooks.push(String(stringHash(book.activityId))));

  downloadedBooks
    .map(downloadedBook => {
      if (remoteBooks.indexOf(downloadedBook) === -1) {
        return join(classFolder, downloadedBook);
      }
    })
    .forEach(obsoleteBookPath => remove(obsoleteBookPath));
}

export let readFile = filePath => {
  return fs.readJsonSync(filePath);
};

export function getActivityContentFilePathsByMode(context, format) {
  const appFolderPath = contentPathUtils.getClassroomAppPath();
  const activityFilePath = contentPathUtils.getActivityContentFilePath(context, format);
  const activityFolderPath = contentPathUtils.getActivityContentDestinationPath(context);

  return { activityFilePath, activityFolderPath, appFolderPath };
}

export function getTemporaryResources(cmi5, userId, classroomId) {
  let resources = [];
  if (cmi5) {
    const book = cmi5.course;
    const units = cmi5.auOrBlock;
    units.forEach(unit => {
      const context = {
        userId,
        classId: classroomId,
        bookActivityId: book.id,
        unitActivityId: unit.id
      };
      if (unit.mode === BOOK_LAUNCH_MODES.SMART || unit.launcher) {
        const activityFilePath = contentPathUtils.getUnitZipFilePath(context);
        resources.push(getTemporaryFileName(activityFilePath));
      }
      unit.auOrBlock.forEach(lesson => {
        if (lesson.mode === BOOK_LAUNCH_MODES.NONE && lesson.launcher) {
          context.lessonActivityId = lesson.id;
          const activityFilePath = contentPathUtils.getActivityContentFilePath(context, lesson.format);
          resources.push(getTemporaryFileName(activityFilePath));
        }
      });
    });
  }
  return resources;
}

export let isBookContentDownloaded = (bookContext, bookCmi5) => {
  const bookDownloadCheckFile = contentPathUtils.getBookDownloadCheckFile(bookContext);
  try {
    let existTemporaryFiles = false;
    if (bookCmi5) {
      const temporaryResources = getTemporaryResources(bookCmi5, bookContext.userId, bookContext.classId);
      temporaryResources.forEach(file => {
        if (existsFile(file)) {
          existTemporaryFiles = true;
          return;
        }
      });
    }

    // Con contenidos muy pequeños no se llegaba a borrar este localstorage
    // Con el refactor esto no existirá
    // const localStoragePercent = JSON.parse(localStorage.getItem(`saveBook_${bookContext.bookId}`));
    const localStoragePercent = false;
    return !localStoragePercent && !existTemporaryFiles && existsFile(bookDownloadCheckFile);
  } catch (err) {
    console.error('[isBookContentDownloaded] Error %s', err);
    return false;
  }
};

export let isUnitContentDownloaded = (unitContext, bookCmi5) => {
  const unitDownloadCheckFile = contentPathUtils.getUnitDownloadCheckFile(unitContext);
  try {
    let existTemporaryFiles = false;
    if (bookCmi5) {
      const temporaryResources = getTemporaryResources(bookCmi5, unitContext.userId, unitContext.classId);
      temporaryResources.forEach(file => {
        if (existsFile(file)) {
          existTemporaryFiles = true;
          return;
        }
      });
    }
    const localStoragePercent = JSON.parse(localStorage.getItem(`saveBook_${unitContext.bookId}`));
    return !localStoragePercent && !existTemporaryFiles && existsFile(unitDownloadCheckFile);
  } catch (err) {
    console.error('[isUnitContentDownloaded] Error %s', err);
    return false;
  }
};

export let isBookOutdated = (bookCmi5, bookContext, singleUnitModeUnitId) => {
  const units = bookCmi5.auOrBlock;
  let hasUpdates = [];
  let removedActivities = [];
  units.forEach(unit => {
    let unitHasChanges = false;
    if (!singleUnitModeUnitId || unit.id === singleUnitModeUnitId) {
      const unitContext = Object.assign(bookContext, { unitActivityId: unit.id });
      const unitCmi5File = contentPathUtils.getUnitCmi5FilePath(unitContext);
      if (existsFile(unitCmi5File)) {
        const unitLocalCmi5 = readFile(unitCmi5File);

        if (unitLocalCmi5.sha256 !== unit.sha256) {
          hasUpdates.push(unit);
          unitHasChanges = true;
        }

        const lessons = unit.auOrBlock;
        lessons.forEach(lesson => {
          let lessonLocalCmi5;
          let lessonLocalIndex;
          unitLocalCmi5.auOrBlock.forEach((lessonCmi5, index) => {
            if (lessonCmi5.id === lesson.id) {
              lessonLocalCmi5 = lessonCmi5;
              lessonLocalIndex = index;
            }
          });
          if (lessonLocalCmi5 && !_.isNil(lessonLocalIndex)) {
            unitLocalCmi5.auOrBlock.splice(lessonLocalIndex, 1);
          }
          if (
            (!lessonLocalCmi5 && lesson.format !== FILE_TYPES.LNK.mimeType) ||
            (lessonLocalCmi5 && lesson.fromCatalog && lesson.sha256 && lessonLocalCmi5.sha256 !== lesson.sha256)
          ) {
            hasUpdates.push(lesson);
            unitHasChanges = true;
          }
        });
        if (unitLocalCmi5.auOrBlock.length > 0) {
          removedActivities.push(...unitLocalCmi5.auOrBlock);
          unitHasChanges = true;
        }
        const { hasAuthoringResourcesUpdates, removedAuthoringResources } = contentUtils.checkContentAuthoringResources(
          unitLocalCmi5,
          unit
        );
        if (hasAuthoringResourcesUpdates.length || removedAuthoringResources.length) {
          unitHasChanges = true;
        }
        hasUpdates.push(...hasAuthoringResourcesUpdates);
        removedActivities.push(...removedAuthoringResources);

        if (!unitHasChanges && unit.authoring !== unitLocalCmi5.authoring) {
          saveOrUpdateOfflineCmi5(bookContext, bookCmi5);
        }
      } else {
        hasUpdates.push(unit);
      }
    }
  });
  return { hasUpdates, removedActivities };
};

export async function saveScaResponseToFile(unitContext, scaJson) {
  try {
    const unitScaFilePath = contentPathUtils.getUnitScaFilePath(unitContext);

    return saveJsonToFile(unitScaFilePath, scaJson);
  } catch (err) {
    debug.error('[saveScaResponseToFile]', err);
    throw err;
  }
}

export async function updateScaWithLocalResources(scaJson, unitContext, customerId) {
  try {
    const scaFileData = Object.assign({}, scaJson);

    scaFileData.contentUrl = contentPathUtils.getUnitIndexFilePath(unitContext);
    scaFileData.services.getCmi5Service = contentPathUtils.getUnitCmi5FilePath(unitContext);
    scaFileData.services.classroomComponentsService = contentPathUtils.getUnitClassroomComponentsFilePath(unitContext);
    scaFileData.skinPath = contentPathUtils.getSkinServiceUrlFilePath(customerId);
    // This line should be the last because the SHA has to be calculated at the end
    scaFileData.sha256 = contentUtils.getScaResponseSha(scaFileData);
    return scaFileData;
  } catch (err) {
    debug.error('[updateScaWithLocalResources]', err);
    throw err;
  }
}

export async function updateScaWithLaunchParameters(unitScaPath, lesson, user, questionActivityId) {
  let scaJson = await readJsonFile(unitScaPath);

  if (lesson && lesson.url) {
    scaJson.lessonUri = lesson.activityId;
  } else {
    scaJson.lessonUri = null;
  }
  scaJson.oauth2Token = user.authToken;
  scaJson.sca.stomp.credentials.passcode = user.authToken;
  scaJson.refreshToken = user.refreshToken;
  scaJson.language = user.language;
  scaJson = { ...scaJson, activityUri: questionActivityId };

  await saveJsonToFile(unitScaPath, scaJson);
}

export const saveOrUpdateUnitNotes = async function(unitContext, unitNotes) {
  let existingNotes;
  const notesFile = contentPathUtils.getUnitNotesFilePath(unitContext);
  if (existsFile(notesFile)) {
    existingNotes = readFile(notesFile);
    const combinedNotesJson = combineLocalAndRemoteNotes(existingNotes, unitNotes);
    await removeFile(notesFile);
    await saveJsonToFile(notesFile, combinedNotesJson);
  } else {
    await saveJsonToFile(notesFile, unitNotes);
  }

  function combineLocalAndRemoteNotes(localNotes, remoteNotes) {
    for (let lesson in remoteNotes) {
      if (!_.isNil(localNotes[lesson])) {
        if (localNotes[lesson].timestamp < remoteNotes[lesson].timestamp) {
          localNotes[lesson] = remoteNotes[lesson];
        }
      } else {
        localNotes[lesson] = remoteNotes[lesson];
      }
    }
    return localNotes;
  }
};

export let saveOrUpdateOfflineCmi5 = async function(bookContext, bookCmi5) {
  let cmi5 = JSON.parse(JSON.stringify(bookCmi5));

  for (const unit of cmi5.auOrBlock) {
    const unitContext = _.cloneDeep(bookContext);
    unitContext.unitActivityId = unit.id;
    let unitLocalCmi5;
    const unitCmi5File = contentPathUtils.getUnitCmi5FilePath(unitContext);
    const unitNotes = await contentUtils.getUnitNotes(
      unitContext.classId,
      unitContext.unitActivityId,
      unitContext.userId
    );
    if (!_.isEmpty(unitNotes)) {
      await saveOrUpdateUnitNotes(unitContext, unitNotes);
    }
    if (existsFile(unitCmi5File)) {
      unitLocalCmi5 = readFile(unitCmi5File);
    }
    if (unit.launcher || unit.fromCatalog) {
      if (!unit.launcher && unitLocalCmi5) {
        setLocalProperties(unit, unitLocalCmi5);
      } else {
        const { launcherDirectoryPath, localLauncher } = updateLocalLauncher(unitContext, unit.mode, unit.launcher);
        unit.launcher = localLauncher;
        unit.launcherDirectoryPath = launcherDirectoryPath;
      }
    }
    unit.auOrBlock.forEach(lesson => {
      if (!lesson.fromCatalog && lesson.format !== FILE_TYPES.LNK.mimeType) {
        lesson.launcher = lesson.title[_.findLastKey(lesson.title)];
        lesson.resourceUrl = lesson.url;
        lesson.mode = BOOK_LAUNCH_MODES.NONE;
      }
      let hasBeenSetLocalProperties = false;
      if (lesson.launcher || lesson.fromCatalog) {
        if (!lesson.launcher && unitLocalCmi5) {
          unitLocalCmi5.auOrBlock.forEach(lessonLocalCmi5 => {
            if (lessonLocalCmi5.id === lesson.id) {
              setLocalProperties(lesson, lessonLocalCmi5);
              hasBeenSetLocalProperties = true;
              return;
            }
          });
        }
        if (!hasBeenSetLocalProperties) {
          const lessonContext = createLessonContext(
            bookContext.userId,
            bookContext.classId,
            bookContext.bookId,
            cmi5.course.id,
            unit.id,
            lesson.id
          );
          const { launcherDirectoryPath, localLauncher } = updateLocalLauncher(
            lessonContext,
            lesson.mode,
            lesson.launcher
          );
          if (!lesson.mode && lesson.fromCatalog) {
            lesson.mode = unit.mode || BOOK_LAUNCH_MODES.NONE;
          }
          lesson.launcherDirectoryPath = launcherDirectoryPath;
          lesson.launcher = localLauncher;
          // The url param is used by SmartContent to launch lesson assets instead of using launcher param
          lesson.url = localLauncher;
        }
      }
    });
    setResourcesAndAuthoring(unit, unitContext);
    const unitCmi5FilePath = contentPathUtils.getUnitCmi5FilePath(unitContext);
    await saveJsonToFile(unitCmi5FilePath, unit);
  }
  return cmi5;
};

export const saveOrUpdateOfflineComponents = async function(unit, bookContext) {
  const unitContext = Object.assign({}, bookContext, { unitActivityId: unit.id || unit.activityId });

  const components = await contentUtils.getClassroomComponentsByUnitId(unitContext.classId, unitContext.unitActivityId);

  const componentsFilePath = contentPathUtils.getUnitClassroomComponentsFilePath(unitContext);
  await saveJsonToFile(componentsFilePath, components);
};

export const saveOrUpdateResources = async function(unit, bookContext) {
  const unitContext = Object.assign({}, bookContext, { unitActivityId: unit.id || unit.activityId });

  const resources = await contentUtils.getResourcesByUnitId(
    unitContext.customerId,
    unit.auOrBlock.map(lesson => lesson.id)
  );

  const bookFolderPath = contentPathUtils.getBookFolderPath(unitContext);

  await Promise.all(
    resources.map(async resource => {
      const filePath = path.join(bookFolderPath, resource.path);
      const folderPath = filePath.substring(0, filePath.lastIndexOf('/'));

      if (!existsFile(folderPath)) {
        await createFolder(folderPath);
      }

      request
        .get(resource.downloadUrl)
        .on('error', err => {
          console.error(err);
        })
        .pipe(fs.createWriteStream(filePath))
        .on('finish', () => {
          console.log('Recurso guardado en ' + filePath);
        });
    })
  );
};

function setLocalProperties(activity, activityLocalCmi5) {
  activity.launcher = activityLocalCmi5.launcher;
  activity.launcherDirectoryPath = activityLocalCmi5.launcherDirectoryPath;
  activity.mode = activityLocalCmi5.mode;
  activity.sha256 = activityLocalCmi5.sha256;
}

function setResourcesAndAuthoring(unit, unitContext) {
  if (unit.resources) {
    const resources = unit.resources;
    for (const key in resources) {
      const resource = resources[key];
      const fileName = resource.url.split('/').pop();
      const fileFormat = resource.url
        .split('/')
        .pop()
        .split('.')
        .pop();
      const assetName = contentPathUtils.getHashedActivityId(fileName);
      const { localLauncher } = updateLocalLauncher(
        unitContext,
        BOOK_LAUNCH_MODES.NONE,
        `authoringResources/${assetName}.${fileFormat}`
      );
      resource.resourceUrl = resource.url;
      resource.url = localLauncher;
      unit.resources[key] = resource;
    }
  }
  contentUtils.updateAuthoringObject(unit);
}

export function synchronizeCdn(authToken, context, forceDownload) {
  const cdnZipFilePath = contentPathUtils.getCdnZipPath(context);
  const cdnFolderPath = contentPathUtils.getCdnFolderPath(context);
  const cdnUrl = `${API_CDN_DOWNLOAD}?version=${v4()}`;
  const temporaryFilePath = getTemporaryFileName(cdnZipFilePath);
  if (existsFile(cdnZipFilePath)) {
    removeFile(cdnZipFilePath);
    removeFile(temporaryFilePath);
  }

  return new Promise(async (resolve, reject) => {
    try {
      if (forceDownload || !existsFile(cdnFolderPath) || !(await isLocalCdnUpdated(context))) {
        downloadFile(authToken, cdnUrl, cdnZipFilePath, cdnFolderPath)
          .then(resolve)
          .catch(reject);
      } else {
        resolve({});
      }
    } catch (error) {
      reject(error);
    }
  });
}

export function synchronizeSkin(authToken, customerId) {
  const parentFolderPath = contentPathUtils.getSkinServiceParentFolderPath(customerId);
  const skinFolderPath = contentPathUtils.getSkinFolderPath(customerId);
  const skinZipFilePath = contentPathUtils.getSkinZipFilePath(customerId);
  const skinUrl = API_BACKOFFICE_SKIN_DOWNLOAD.replace(PARAMS.CUSTOMER, customerId);
  const skinDownloadedStatus = getDownloadStatus(skinFolderPath, skinZipFilePath);
  const isSkinDownloaded = skinDownloadedStatus === BOOK_DOWNLOADING_STATUS.COMPLETED;

  return new Promise(async (resolve, reject) => {
    try {
      if (skinDownloadedStatus === BOOK_DOWNLOADING_STATUS.DOWNLOADING) {
        reject();
      } else if (isSkinDownloaded && (await isLocalSkinUpdated(customerId))) {
        resolve({});
      } else {
        downloadFile(authToken, skinUrl, skinZipFilePath, skinFolderPath, parentFolderPath)
          .then(() => {
            removeFile(skinZipFilePath);
            resolve({});
          })
          .catch(err => {
            console.log(err);
            reject(err);
          });
      }
    } catch (error) {
      reject(error);
    }
  });
}

export function removeResources(resources) {
  resources.forEach(resource => {
    if (resource.launcherDirectoryPath) {
      fs.removeSync(resource.launcherDirectoryPath);
    } else {
      removeFile(resource.url);
    }
  });
}

function getDownloadStatus(folderPath, zipFilePath) {
  const isDownloaded = existsFile(folderPath);
  const isUnzipped = !existsFile(zipFilePath);

  const temporaryFilePath = getTemporaryFileName(zipFilePath);
  const cleanDownloaded = !existsFile(temporaryFilePath);

  if (isDownloaded && isUnzipped && cleanDownloaded) {
    return BOOK_DOWNLOADING_STATUS.COMPLETED;
  } else if (isDownloaded && (!isUnzipped || !cleanDownloaded)) {
    return BOOK_DOWNLOADING_STATUS.DOWNLOADING;
  }
  return BOOK_DOWNLOADING_STATUS.EMPTY;
}

async function isLocalCdnUpdated(context) {
  try {
    const cdnRemoteVersionResponse = await getCdnVersion();
    const cdnRemoteVersion = cdnRemoteVersionResponse.data.version;
    const cdnLocalVersion = await getLocalCdnVersion(context);

    debug(`cdnRemoteVersion: "${cdnRemoteVersion}" - cdnLocalVersion: "${cdnLocalVersion}"`);

    if (versionCompare(cdnRemoteVersion, cdnLocalVersion) === VERSION_COMPARE.V1_EQUAL_TO_V2) {
      debug('CDN is updated');
      return true;
    }
    debug('CDN is not updated');
    return false;
  } catch (err) {
    debug.error('[isLocalCdnUpdated]', err);
    return false;
  }
}

async function isLocalSkinUpdated(customerId) {
  try {
    const skinRemoteVersionResponse = await getSkinVersion(customerId);
    const skinRemoteVersion = skinRemoteVersionResponse.data;
    const skinLocalVersion = await getLocalSkinVersion(customerId);

    debug(`skinRemoteVersion: "${skinRemoteVersion}" - skinLocalVersion: "${skinLocalVersion}"`);

    if (
      !_.isEmpty(skinRemoteVersion) &&
      versionCompare(skinRemoteVersion, skinLocalVersion) === VERSION_COMPARE.V1_EQUAL_TO_V2
    ) {
      debug('Skin is already updated');
      return Promise.resolve(true);
    }
    debug('Skin is not updated');
    return Promise.resolve(false);
  } catch (err) {
    debug.error('[isLocalSkinUpdated]', err);
    return Promise.reject(err);
  }
}

async function getLocalCdnVersion(context) {
  const cdnVersionFilePath = contentPathUtils.getCdnVersionFilePath(context);
  const cdnPackageJson = await readJsonFile(cdnVersionFilePath);

  return cdnPackageJson.version;
}

/**
 * Compares two string version values.
 *
 * Returns:
 * -1 = version1 < version2
 *  0 = version1 = version2
 *  1 = version1 > version2
 *  FALSE if one of input versions are not valid
 *
 * @param {String} version1
 * @param {String} version2
 * @return {Integer|Boolean}
 */
function versionCompare(version1, version2) {
  if (typeof version1 !== 'string' || typeof version2 !== 'string') {
    return VERSION_COMPARE.ERROR;
  }

  const a = version1.split('.');
  const b = version2.split('.');
  const len = Math.max(a.length, b.length);
  let i = 0;

  for (; i < len; i++) {
    if ((a[i] && !b[i] && parseInt(a[i], 10) > 0) || parseInt(a[i], 10) > parseInt(b[i], 10)) {
      return VERSION_COMPARE.V1_GREATER_THAN_V2;
    } else if ((b[i] && !a[i] && parseInt(b[i], 10) > 0) || parseInt(a[i], 10) < parseInt(b[i], 10)) {
      return VERSION_COMPARE.V1_LOWER_THAN_V2;
    }
  }
  return VERSION_COMPARE.V1_EQUAL_TO_V2;
}

async function getLocalSkinVersion(context) {
  const skinVersionFilePath = contentPathUtils.getSkinVersionFilePath(context);
  const skinVersionJson = await readJsonFile(skinVersionFilePath);

  return skinVersionJson ? skinVersionJson.version : skinVersionJson;
}

function updateLocalLauncher(bookContext, mode, launcher, format) {
  const { activityFilePath, activityFolderPath, appFolderPath } = getActivityContentFilePathsByMode(
    bookContext,
    mode,
    format
  );
  let localLauncher = '';
  let launcherDirectoryPath = activityFolderPath;

  if (mode === BOOK_LAUNCH_MODES.SMART) {
    localLauncher = `${appFolderPath}/${launcher}`;
  } else {
    if (format === FILE_TYPES.ZIP.mimeType) {
      localLauncher = activityFilePath;
    } else {
      localLauncher = `${activityFolderPath}/${launcher}`;
    }
  }
  return { launcherDirectoryPath, localLauncher };
}

function createLessonContext(userId, classId, bookId, bookActivityId, unitActivityId, lessonActivityId, bookName) {
  return { userId, classId, bookId, bookActivityId, unitActivityId, lessonActivityId, bookName };
}
